// src/services/vehicleServices.ts
import api from "../lib/api.ts";

interface VehiclePaginationParams {
  page?: number;
  limit?: number;
  orderBy?: string;
  order?: "ASC" | "DESC";
  type?: string;
  auctionId?: number;
}

interface VehicleData {
  makeId: number;
  modelId: number;
  year: number;
  vinNumber: string;
  damageTypeId: number;
  mileage: number;
  startingPrice: number;
  bodyTypeId?: number;
  fuelTypeId?: number;
  transmissionTypeId?: number;
  drivelineTypeId?: number;
  exteriorColorId?: number;
  interiorColorId?: number;
  auctionId?: number;
  reservePrice?: number;
  description?: string;
  notes?: string;
  sellerId: number;
  sellerType: string;
}

interface VehicleReturnRequestData {
  vehicleId: number;
}

export const getPaginatedProducts = async (params: VehiclePaginationParams) => {
  const response = await api.get("/products/paginated", { params });
  return response.data;
};

// Add new vehicle
export const addVehicle = async (vehicleData: FormData) => {
  try {
    const response = await api.post("/products/seller", vehicleData, {
      // headers: {
      //   "Content-Type": "multipart/form-data",
      // },
    });

    return response.data;
  } catch (error) {
    console.error("Error adding vehicle:", error);
    throw error;
  }
};

// Get vehicle by ID
export const getVehicleById = async (vehicleId: number) => {
  try {
    const response = await api.get(`/products/seller/${vehicleId}`);
    return response.data;
  } catch (error) {
    console.error("Error fetching vehicle:", error);
    throw error;
  }
};

// Get seller's vehicles
export const getSellerVehicles = async (
  sellerId: number,
  params?: VehiclePaginationParams
) => {
  try {
    const response = await api.get(`/vehicles/seller/${sellerId}`, { params });
    return response.data;
  } catch (error) {
    console.error("Error fetching seller vehicles:", error);
    throw error;
  }
};

// Update vehicle
export const updateVehicle = async (
  vehicleId: number,
  vehicleData: Partial<VehicleData>
) => {
  try {
    const response = await api.put(
      `/products/seller/${vehicleId}`,
      vehicleData
    );
    return response.data;
  } catch (error) {
    console.error("Error updating vehicle:", error);
    throw error;
  }
};

// Get vehicle makes
export const getVehicleMakes = async () => {
  try {
    const response = await api.get("/makes");
    return response.data;
  } catch (error) {
    console.error("Error fetching makes:", error);
    throw error;
  }
};

// Get vehicle models by make
export const getVehicleModels = async (makeId: number) => {
  try {
    const response = await api.get(`/models?makeId=${makeId}`);
    return response.data;
  } catch (error) {
    console.error("Error fetching models:", error);
    throw error;
  }
};

// Get vehicle variants by model
export const getVehicleVariants = async (modelId: number) => {
  try {
    const response = await api.get(`/variants?modelId=${modelId}`);
    return response.data;
  } catch (error) {
    console.error("Error fetching variants:", error);
    throw error;
  }
};

// Get vehicle specifications
export const getVehicleSpecifications = async () => {
  try {
    const [
      bodyTypes,
      fuelTypes,
      highlights,
      specifications,
      transmissionTypes,
      drivelineTypes,
      exteriorColors,
      interiorColors,
      statuses,
      cylinders,
      engines,
      damageTypes,
      seatTypes,
    ] = await Promise.all([
      api.get("/bodytypes"),
      api.get("/fueltypes"),
      api.get("/highlights"),
      api.get("/specifications"),
      api.get("/transmissiontypes"),
      api.get("/drivelinetypes"),
      api.get("/exteriorcolors"),
      api.get("/interiorcolors"),
      api.get("/productstatuses"),
      api.get("/cylinders"),
      api.get("/engines"),
      api.get("/damagetypes"),
      api.get("/seattypes"),
    ]);

    return {
      bodyTypes: bodyTypes.data || [],
      fuelTypes: fuelTypes.data || [],
      highlights: highlights.data || [],
      specifications: specifications.data || [],
      transmissionTypes: transmissionTypes.data || [],
      drivelineTypes: drivelineTypes.data || [],
      exteriorColors: exteriorColors.data || [],
      interiorColors: interiorColors.data || [],
      statuses: statuses.data || [],
      cylinders: cylinders.data || [],
      engines: engines.data || [],
      damageTypes: damageTypes.data || [],
      seatTypes: seatTypes.data || [],
    };
  } catch (error) {
    console.error("Error fetching vehicle specifications:", error);
    throw error;
  }
};

// Get upcoming auctions
export const getUpcomingAuctions = async () => {
  try {
    const response = await api.get("/auctions?type=upcoming&isActive=true");
    return response.data;
  } catch (error) {
    console.error("Error fetching upcoming auctions:", error);
    throw error;
  }
};

// Upload vehicle images
export const uploadVehicleImages = async (
  vehicleId: number,
  images: File[]
) => {
  try {
    const formData = new FormData();
    formData.append("vehicleId", vehicleId.toString());

    images.forEach((image, index) => {
      formData.append("images", image);
    });

    // Use the correct image upload endpoint
    const response = await api.post(`/image/upload`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
    return response.data;
  } catch (error) {
    console.error("Error uploading vehicle images:", error);
    throw error;
  }
};

export const requestVehicleApproval = async (productId: string) => {
  const response = await api.put(`/products/${productId}/request-approval`);
  return response.data;
};

export const requestVehicleReturn = async (
  params: VehicleReturnRequestData
) => {
  try {
    const response = await api.post(
      "/products/seller/requestreturn",
      params,
      {}
    );

    return response.data;
  } catch (error) {
    console.error("Error requesting vehicle return:", error);
    throw error;
  }
};
